/**
 * AssetsCatalogSection — Catálogo Maestro · Activos (V1.44.1)
 * Gestiona los activos REALES (/api/assets): son los elementos que alimentan
 * los desplegables "Cuenta origen/destino" (tipos líquidos) y el Patrimonio.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Layers, Info } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { refreshCatalogs, LIQUID_ASSET_TYPES } from "./MoneyMovementFields";

const num = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

export default function AssetsCatalogSection({ testPrefix = "ca-activo" }) {
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("Bancos y Caja");
  const [balance, setBalance] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/assets").then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
    api.get("/asset_types").then((r) => setTypes(r.data.filter((t) => t.active).map((t) => t.name))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const afterChange = () => { refreshCatalogs(); load(); };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/assets", { name: name.trim(), type, balance: num(balance) });
      toast.success(`Activo "${name.trim()}" creado${LIQUID_ASSET_TYPES.has(type) ? " — ya disponible como cuenta origen/destino" : ""}`);
      setName(""); setBalance("");
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      await api.put(`/assets/${editing.id}`, { name: editing.name.trim(), type: editing.type, balance: num(editing.balance), purchase_value: editing.purchase_value ?? null, acquisition_date: editing.acquisition_date || null, owner_member_id: editing.owner_member_id || null, notes: editing.notes || null });
      toast.success("Activo actualizado");
      setEditing(null);
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar el activo "${it.name}"?\n\nDejará de contar en Patrimonio y de aparecer como cuenta en los formularios. Los movimientos históricos no se pierden.`)) return;
    try {
      await api.delete(`/assets/${it.id}`);
      toast.success("Activo eliminado");
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => {
    const map = {};
    (items || []).forEach((a) => { const k = a.type || "Sin tipo"; (map[k] = map[k] || []).push(a); });
    const keys = Object.keys(map).sort((a, b) => a.localeCompare(b, "es"));
    keys.forEach((k) => map[k].sort((a, b) => a.name.localeCompare(b.name, "es")));
    return { map, keys };
  }, [items]);

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid={`${testPrefix}-section`}>
      <div>
        <h3 className="font-heading font-bold text-lg">Activos</h3>
        <p className="text-xs text-muted-foreground">Activos y cuentas reales de la familia (Bankinter, Revolut, Vivienda Principal, Nissan Leaf…). Son los mismos que gestiona el módulo Activos y alimentan el Patrimonio.</p>
      </div>
      <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-secondary/40 border border-border rounded-md p-2.5" data-testid={`${testPrefix}-hint`}>
        <Info size={13} className="shrink-0 mt-0.5" />
        <span>Los activos de tipo <span className="font-semibold text-foreground">Bancos y Caja, Tarjetas o Wallets y Plataformas de Pago</span> aparecen automáticamente como <span className="font-semibold text-foreground">Cuenta origen / Cuenta destino</span> en los formularios de gastos, ingresos y compromisos.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px_130px_auto] gap-2">
        <Input data-testid={`${testPrefix}-name-input`} placeholder="Nombre (ej. Bankinter, Caja Efectivo…)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid={`${testPrefix}-type-select`}><SelectValue placeholder="Tipo…" /></SelectTrigger>
          <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid={`${testPrefix}-balance-input`} inputMode="decimal" placeholder="Saldo (€)" value={balance} onChange={(e) => setBalance(e.target.value)} />
        <Button data-testid={`${testPrefix}-create-btn`} onClick={create} disabled={!name.trim()}><Plus size={15} className="mr-1" /> Crear</Button>
      </div>
      {items === null ? (
        <div className="p-4 text-sm text-muted-foreground">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Sin activos. Crea el primero arriba (ej. tu cuenta bancaria con su saldo actual).</div>
      ) : (
        <div className="space-y-3">
          {grouped.keys.map((k) => (
            <div key={k} className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-secondary/40 text-xs font-semibold text-muted-foreground flex items-center gap-2">
                <Layers size={12} /> {k} <span className="opacity-60">({grouped.map[k].length})</span>
                {LIQUID_ASSET_TYPES.has(k) && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400 px-1.5 py-0.5 rounded">Cuenta origen/destino</span>}
              </div>
              <div className="divide-y divide-border">
                {grouped.map[k].map((it) => (
                  <div key={it.id} className="flex items-center justify-between px-3 py-2 hover:bg-secondary/30" data-testid={`${testPrefix}-row-${it.id}`}>
                    <span className="text-sm font-medium truncate">{it.name}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm tabular text-muted-foreground">{(it.balance ?? 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</span>
                      <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => setEditing({ ...it, balance: String(it.balance ?? "") })} className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                      <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => remove(it)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar activo</DialogTitle>
            <DialogDescription className="text-xs">Cambios de saldo y tipo se reflejan en Patrimonio y en los desplegables de cuentas.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid={`${testPrefix}-edit-name`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
              <div>
                <label className="text-xs text-muted-foreground">Tipo</label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger data-testid={`${testPrefix}-edit-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Saldo (€)</label>
                <Input data-testid={`${testPrefix}-edit-balance`} inputMode="decimal" value={editing.balance} onChange={(e) => setEditing({ ...editing, balance: e.target.value })} />
              </div>
              <Button data-testid={`${testPrefix}-save`} className="w-full" onClick={save} disabled={!editing.name.trim()}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
